import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Info,
  Loader2,
  RotateCcw,
  User,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePreviewUsageLink } from "../hooks/use-preview-usage-link";
import { useUpdateUsageIdentifier } from "../hooks/use-update-usage-identifier";
import { useUpdateUsageUser } from "@/features/users/hooks/use-update-usage-user";
import { useUpdateUtilizedSessions } from "@/features/users/hooks/use-update-utilized-sessions";
import { useUpdateBundleUsageStatus } from "@/features/users/hooks/use-update-bundle-usage-status";
import { useUpdatePreferredProfessional } from "@/features/users/hooks/use-update-preferred-professional";
import { toast } from "@/hooks/use-toast";
import type { PreviewBundleUser, PreviewProfessionalMatch, UsageLinkPreview } from "../services/preview-usage-link";
import type { BundleUsage } from "@/pages/users/types/user";

type Step = "input" | "preview";
const ALLOWED_STATUSES = ["ACTIVE", "COMPLETED", "EXPIRED"];

function ProfessionalMatchSection({
  match,
  changeProfessional,
  onToggleChangeProfessional,
  canChangeProfessional,
}: {
  match: PreviewProfessionalMatch;
  changeProfessional: boolean;
  onToggleChangeProfessional: (value: boolean) => void;
  canChangeProfessional: boolean;
}) {
  const { session_professional, bundle_professional, will_change } = match;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Professional mapping
      </p>
      <div className="text-xs text-gray-500">
        Session professional:{" "}
        <span className="font-medium text-gray-700">{session_professional.name}</span>
      </div>

      {!bundle_professional ? (
        <div className="flex items-center gap-2 text-sm text-green-700">
          <Briefcase className="h-4 w-4 flex-shrink-0" />
          <span>
            <span className="font-medium">{session_professional.name}</span> will be set as the professional for this bundle.
          </span>
        </div>
      ) : !will_change ? (
        <div className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span>
            <span className="font-medium">{session_professional.name}</span> is already the professional for this bundle.
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 text-sm text-yellow-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Bundle currently has{" "}
              <span className="font-medium">{bundle_professional.name}</span> as professional.
              This session is assigned to{" "}
              <span className="font-medium">{session_professional.name}</span>.
            </span>
          </div>
          {canChangeProfessional ? (
            <label className="flex items-start gap-2 pl-6 text-sm text-gray-800 cursor-pointer">
              <Checkbox
                className="mt-0.5"
                checked={changeProfessional}
                onCheckedChange={(checked) =>
                  onToggleChangeProfessional(checked === true)
                }
              />
              <span>
                Do you want to change the professional to{" "}
                <span className="font-medium">{session_professional.name}</span>?
              </span>
            </label>
          ) : (
            <p className="pl-6 text-xs text-gray-500">
              The bundle professional will <span className="font-medium">not</span> be
              changed automatically. Map a user to this bundle to enable changing it.
            </p>
          )}
        </>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source_id: string;
  current_usage_identifier: string | null | undefined;
  current_bundle: BundleUsage | null | undefined;
}

export function EditUsageIdDialog({
  open,
  onOpenChange,
  source_id,
  current_usage_identifier,
  current_bundle,
}: Props) {
  const [step, setStep] = useState<Step>("input");
  const [usageIdValue, setUsageIdValue] = useState(current_usage_identifier ?? "");
  const [preview, setPreview] = useState<UsageLinkPreview | null>(null);
  const [selectedUserToMap, setSelectedUserToMap] = useState<PreviewBundleUser | null>(null);
  const [utilizedOverride, setUtilizedOverride] = useState<number | null>(null);
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  // Opt-in (checked by default) to also move the bundle's professional to the
  // session's professional when they diverge. See ProfessionalMatchSection.
  const [changeProfessional, setChangeProfessional] = useState(true);

  // Re-sync input value whenever the dialog opens (useState only initializes once)
  useEffect(() => {
    if (open) {
      setUsageIdValue(current_usage_identifier ?? "");
      setStep("input");
      setPreview(null);
      setSelectedUserToMap(null);
      setChangeProfessional(true);
      resetOverrides();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { mutate: fetchPreview, isPending: isLoadingPreview } = usePreviewUsageLink();
  const { mutateAsync: updateUsageIdentifierAsync, isPending: isUpdating } = useUpdateUsageIdentifier();
  const { mutateAsync: updateUsageUserAsync, isPending: isMappingUser } = useUpdateUsageUser();
  const { mutateAsync: updateUtilizedAsync, isPending: isUpdatingUtilized } = useUpdateUtilizedSessions();
  const { mutateAsync: updateBundleStatusAsync, isPending: isUpdatingStatus } = useUpdateBundleUsageStatus();
  const { mutateAsync: updatePreferredProfessionalAsync, isPending: isUpdatingProfessional } = useUpdatePreferredProfessional();

  const isAnyPending = isUpdating || isMappingUser || isUpdatingUtilized || isUpdatingStatus || isUpdatingProfessional;
  const isUnlinkAction = !usageIdValue.trim();

  // ── Professional-change (opt-in) ──────────────────────────────────────────
  // The preferred-professional endpoint sweeps same-standard ACTIVE usages of a
  // mapped user, so it needs a user_id that actually belongs to the bundle.
  // Prefer an already-mapped user; fall back to the one being mapped in this flow.
  const professionalMatch = preview?.professional_match ?? null;
  const professionalUserId =
    preview?.mapped_users?.[0]?.id ?? selectedUserToMap?.id ?? null;
  const canChangeProfessional =
    !!professionalMatch?.will_change && !isUnlinkAction && !!professionalUserId;

  // ── Derived computed values ──────────────────────────────────────────────
  const autoUtilized = preview?.bundle?.projected_utilized ?? null;
  const autoStatus = preview?.bundle?.projected_status ?? null;
  const effectiveUtilized = utilizedOverride ?? autoUtilized;
  const effectiveStatus = statusOverride ?? autoStatus;
  const totalSessions = preview?.bundle?.total_session_quantity ?? 0;

  const willComplete = effectiveUtilized === totalSessions;
  const isExpiredBundle =
    preview?.bundle?.end_date
      ? new Date(preview.bundle.end_date) < new Date()
      : false;

  const isUtilizedChanged = utilizedOverride !== null && utilizedOverride !== autoUtilized;
  const isStatusChanged = statusOverride !== null && statusOverride !== autoStatus;

  // ── Validation ───────────────────────────────────────────────────────────
  const isUtilizedValid =
    effectiveUtilized !== null &&
    Number.isInteger(effectiveUtilized) &&
    effectiveUtilized >= 0 &&
    effectiveUtilized <= totalSessions;

  const hasBlockingErrors = (preview?.errors ?? []).length > 0;
  const isConfirmDisabled =
    hasBlockingErrors ||
    preview?.session.is_already_linked_to_same_bundle ||
    !isUtilizedValid ||
    isAnyPending;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const resetOverrides = () => {
    setUtilizedOverride(null);
    setStatusOverride(null);
  };

  const handleClose = () => {
    setStep("input");
    setPreview(null);
    setSelectedUserToMap(null);
    setChangeProfessional(true);
    setUsageIdValue(current_usage_identifier ?? "");
    resetOverrides();
    onOpenChange(false);
  };

  // ── Preview ──────────────────────────────────────────────────────────────
  const handlePreview = () => {
    const trimmed = usageIdValue.trim();
    resetOverrides();

    if (!trimmed) {
      // Unlink path — build synthetic preview from current_bundle data
      const cb = current_bundle;
      if (!cb) return;

      const projectedUnlinkUtilized = Math.max(0, cb.utilized_session_quantity - 1);
      const isExp = new Date(cb.end_date) < new Date();
      const projectedUnlinkStatus = isExp ? "EXPIRED" : "ACTIVE";
      const unlinkErrors: string[] = [];

      if (cb.utilized_session_quantity <= 0) {
        unlinkErrors.push(
          "Cannot unlink — bundle already has 0 utilized sessions."
        );
      }

      setPreview({
        bundle: {
          id: cb.id,
          name: cb.bundle?.name ?? current_usage_identifier ?? "Bundle",
          status: cb.status,
          end_date: cb.end_date,
          total_session_quantity: cb.total_session_quantity,
          utilized_session_quantity: cb.utilized_session_quantity,
          projected_utilized: projectedUnlinkUtilized,
          projected_status: projectedUnlinkStatus,
          will_complete: false,
        },
        mapped_users: [],
        phone_match: null,
        professional_match: null,
        session: {
          source_id,
          name: "",
          current_usage_identifier: current_usage_identifier ?? null,
          is_already_linked_to_same_bundle: false,
        },
        errors: unlinkErrors,
        warnings:
          unlinkErrors.length === 0
            ? [
                `This will unlink the session from bundle "${cb.bundle.name}" and decrement its utilized count from ${cb.utilized_session_quantity} to ${projectedUnlinkUtilized}.`,
              ]
            : [],
      });
      setStep("preview");
      return;
    }

    // Link path — call the preview API
    fetchPreview(
      { source_id, usage_identifier: trimmed },
      {
        onSuccess: (data) => {
          setPreview(data);
          setStep("preview");
          if (
            data.phone_match &&
            data.phone_match.matched_users.length === 1 &&
            !data.phone_match.is_already_mapped
          ) {
            setSelectedUserToMap(data.phone_match.matched_users[0]);
          }
        },
      }
    );
  };

  // ── Confirm ──────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    const trimmed = usageIdValue.trim();
    const bundleId = preview?.bundle?.id ?? current_usage_identifier;

    try {
      // 1. Map user to bundle (if selected)
      if (selectedUserToMap && bundleId) {
        await updateUsageUserAsync({
          source_id: bundleId,
          user_id: selectedUserToMap.id,
        });
      }

      // 2. Link / unlink the session
      await updateUsageIdentifierAsync({
        source_id,
        usage_identifier: trimmed || null,
      });

      // 3. Override utilized count if user changed it
      if (bundleId && isUtilizedChanged && utilizedOverride !== null) {
        await updateUtilizedAsync({
          usage_id: bundleId,
          new_utilization_session__count: utilizedOverride,
        });
      }

      // 4. Override status if user changed it
      if (bundleId && isStatusChanged && statusOverride !== null) {
        await updateBundleStatusAsync({
          bundle_id: bundleId,
          status: statusOverride,
        });
      }

      // 5. Change the bundle professional to the session's professional (opt-in).
      //    Uses the preferred-professional endpoint so the same-standard sibling
      //    sweep + change-request cleanup happens too. Runs after user-mapping so
      //    the swept user is guaranteed to belong to the bundle.
      if (
        changeProfessional &&
        canChangeProfessional &&
        bundleId &&
        professionalUserId &&
        professionalMatch?.session_professional?.id
      ) {
        await updatePreferredProfessionalAsync({
          usage_id: bundleId,
          user_id: professionalUserId,
          professional_id: professionalMatch.session_professional.id,
        });
      }

      handleClose();
    } catch {
      // Individual hooks already show error toasts; reset to re-preview
      toast({
        title: "Error",
        description:
          "Bundle state may have changed. Please go back and re-preview.",
        variant: "destructive",
      });
      setStep("input");
      setPreview(null);
      resetOverrides();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === "input"
              ? "Edit Usage ID"
              : isUnlinkAction
              ? "Confirm Unlink Bundle"
              : "Confirm Usage ID Link"}
          </DialogTitle>
        </DialogHeader>

        {/* ── Step 1: Input ── */}
        {step === "input" && (
          <>
            <div className="py-4 space-y-2">
              <label className="text-sm font-medium">
                Usage Identifier (Bundle Usage ID)
              </label>
              <div className="relative">
                <Input
                  placeholder="Enter usage identifier"
                  value={usageIdValue}
                  onChange={(e) => setUsageIdValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handlePreview()}
                  className="font-mono pr-8"
                  autoFocus
                />
                {usageIdValue && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setUsageIdValue("")}
                    tabIndex={-1}
                    aria-label="Clear"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {current_usage_identifier && (
                <p className="text-xs text-gray-500">
                  Currently linked:{" "}
                  <span className="font-mono">{current_usage_identifier}</span>.{" "}
                  Clear the field to unlink.
                </p>
              )}
              {!current_usage_identifier && (
                <p className="text-xs text-gray-500">
                  No bundle currently linked. Enter an ID to link one.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={
                  isLoadingPreview ||
                  (!usageIdValue.trim() && !current_usage_identifier)
                }
                variant={isUnlinkAction ? "destructive" : "default"}
              >
                {isLoadingPreview ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading preview…
                  </>
                ) : isUnlinkAction ? (
                  "Preview unlink"
                ) : (
                  <>
                    Preview
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Step 2: Preview / Confirmation ── */}
        {step === "preview" && preview && (
          <>
            <div className="py-2 space-y-4 max-h-[60vh] overflow-y-auto pr-1">

              {/* Blocking Errors */}
              {preview.errors.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                  {preview.errors.map((e) => (
                    <div
                      key={e}
                      className="flex items-start gap-2 text-sm text-red-700"
                    >
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {e}
                    </div>
                  ))}
                </div>
              )}

              {/* Bundle Summary + Editable Overrides */}
              {preview.bundle && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">
                      {preview.bundle.name}
                    </span>
                    <Badge
                      className={
                        preview.bundle.status === "ACTIVE"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-red-100 text-red-800 border-red-200"
                      }
                    >
                      {preview.bundle.status}
                    </Badge>
                  </div>

                  <div className="text-xs text-gray-500">
                    Expires:{" "}
                    {format(new Date(preview.bundle.end_date), "MMM d, yyyy")}
                  </div>

                  {/* Current sessions display */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sessions currently used</span>
                    <span className="font-mono text-gray-900">
                      {preview.bundle.utilized_session_quantity} /{" "}
                      {totalSessions}
                    </span>
                  </div>

                  {/* Editable: Utilized sessions after action */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-600">
                        After this action
                        {isUtilizedChanged && (
                          <Badge className="ml-2 text-xs bg-blue-100 text-blue-700 border-blue-200">
                            Custom
                          </Badge>
                        )}
                      </label>
                      {isUtilizedChanged && (
                        <button
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                          onClick={() => setUtilizedOverride(null)}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Reset
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={totalSessions}
                        step={1}
                        value={effectiveUtilized ?? ""}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          setUtilizedOverride(isNaN(val) ? null : val);
                        }}
                        className={`w-20 font-mono text-center ${
                          !isUtilizedValid
                            ? "border-red-400 focus-visible:ring-red-400"
                            : willComplete
                            ? "border-orange-400"
                            : "border-green-400"
                        }`}
                      />
                      <span className="text-sm text-gray-500">
                        / {totalSessions}
                      </span>
                      {willComplete && (
                        <Badge className="text-xs bg-orange-100 text-orange-800 border-orange-200">
                          Will complete bundle
                        </Badge>
                      )}
                    </div>
                    {!isUtilizedValid && effectiveUtilized !== null && (
                      <p className="text-xs text-red-600">
                        Must be a whole number between 0 and {totalSessions}.
                      </p>
                    )}
                    {!isUtilizedChanged && (
                      <p className="text-xs text-gray-400">
                        Auto-computed: {autoUtilized}. Edit to override.
                      </p>
                    )}
                  </div>

                  {/* Editable: Bundle status after action */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-600">
                        Bundle status after
                        {isStatusChanged && (
                          <Badge className="ml-2 text-xs bg-blue-100 text-blue-700 border-blue-200">
                            Custom
                          </Badge>
                        )}
                      </label>
                      {isStatusChanged && (
                        <button
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                          onClick={() => setStatusOverride(null)}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Reset
                        </button>
                      )}
                    </div>
                    <Select
                      value={effectiveStatus ?? ""}
                      onValueChange={(val) => setStatusOverride(val)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {ALLOWED_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isStatusChanged && (
                      <p className="text-xs text-gray-400">
                        Auto-computed: {autoStatus}. Change to override.
                      </p>
                    )}

                    {/* Status-specific warnings */}
                    {effectiveStatus === "COMPLETED" &&
                      effectiveUtilized !== null &&
                      effectiveUtilized < totalSessions && (
                        <div className="flex items-start gap-2 text-xs text-yellow-700 mt-1">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                          Marking COMPLETED with unused sessions remaining is
                          unusual.
                        </div>
                      )}
                    {effectiveStatus === "ACTIVE" && isExpiredBundle && (
                      <div className="flex items-start gap-2 text-xs text-yellow-700 mt-1">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        Bundle expiry has passed — ACTIVE may be auto-corrected
                        by server logic.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Already-linked info */}
              {preview.session.is_already_linked_to_same_bundle && (
                <div className="flex items-start gap-2 text-sm text-blue-700 rounded-md border border-blue-200 bg-blue-50 p-3">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  This session is already linked to this bundle — no change will
                  occur.
                </div>
              )}

              {/* Warnings */}
              {preview.warnings.filter(
                (w) => !w.includes("already linked to this bundle")
              ).length > 0 && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 space-y-1">
                  {preview.warnings
                    .filter((w) => !w.includes("already linked to this bundle"))
                    .map((w) => (
                      <div
                        key={w}
                        className="flex items-start gap-2 text-sm text-yellow-800"
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {w}
                      </div>
                    ))}
                </div>
              )}

              {/* Currently mapped users */}
              {preview.mapped_users.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Currently mapped users
                  </p>
                  {preview.mapped_users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2 text-sm text-gray-800"
                    >
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{u.full_name}</span>
                      {u.preferred_phone_number && (
                        <span className="text-gray-400 text-xs">
                          {u.preferred_phone_number}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Professional mapping */}
              {preview.professional_match && !isUnlinkAction && (
                <ProfessionalMatchSection
                  match={preview.professional_match}
                  changeProfessional={changeProfessional}
                  onToggleChangeProfessional={setChangeProfessional}
                  canChangeProfessional={canChangeProfessional}
                />
              )}

              {/* Phone match / user mapping */}
              {preview.phone_match && (
                <div className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    User mapping
                  </p>
                  <div className="text-xs text-gray-500">
                    Invitee phone:{" "}
                    <span className="font-mono text-gray-700">
                      {preview.phone_match.invitee_phone}
                    </span>
                  </div>

                  {preview.phone_match.is_already_mapped ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      Invitee is already mapped to this bundle.
                    </div>
                  ) : preview.phone_match.matched_users.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <User className="h-4 w-4 flex-shrink-0" />
                      No user found matching this phone number.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600">
                        Select a user to map to this bundle:
                      </p>
                      {preview.phone_match.matched_users.map((u) => (
                        <label
                          key={u.id}
                          className="flex items-center gap-3 cursor-pointer rounded border border-gray-100 p-2 hover:bg-gray-50"
                        >
                          <Checkbox
                            checked={selectedUserToMap?.id === u.id}
                            onCheckedChange={(checked) =>
                              setSelectedUserToMap(checked ? u : null)
                            }
                          />
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {u.full_name}
                            </div>
                            {u.preferred_phone_number && (
                              <div className="text-xs text-gray-500">
                                {u.preferred_phone_number}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notification note — only for link actions */}
              {!isUnlinkAction && (
                <div className="flex items-start gap-2 text-xs text-gray-500 rounded-md border border-gray-100 bg-gray-50 p-3">
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Booking notifications will be sent only if no notifications
                  exist yet for this session, the session is within the next 2
                  days, and the current time is after 8am IST.
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setStep("input");
                  setPreview(null);
                  setSelectedUserToMap(null);
                  resetOverrides();
                }}
                disabled={isAnyPending}
              >
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                variant={isUnlinkAction ? "destructive" : "default"}
              >
                {isAnyPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
